import React, { useEffect, useState } from "react";
import MoreFields from "./MoreFields";
import { motion } from "framer-motion";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Box,
  Drawer,
  IconButton,
  TextField,
  MenuItem,
  InputAdornment,
  Typography,
  Divider,
  Button,
  Alert,
} from "@mui/material";
import {
  MdClose,
  MdInventory2,
  MdAttachMoney,
  MdNumbers,
  MdCategory,
  MdDescription,
  MdQrCode,
  MdPercent,
} from "react-icons/md";

const validationSchema = Yup.object().shape({
  product_name: Yup.string().required("Product name is required"),
  mrp: Yup.number()
    .required("MRP is required")
    .min(0, "MRP must be greater than or equal to 0"),
  selling_price: Yup.number()
    .required("Selling price is required")
    .min(0, "Selling price must be greater than or equal to 0")
    .test(
      "less-than-mrp",
      "Selling price should be less than or equal to MRP",
      function (value) {
        const { mrp } = this.parent;
        return !mrp || !value || value <= mrp;
      }
    ),
  stock_quantity: Yup.number()
    .min(0, "Stock quantity must be greater than or equal to 0")
    .integer("Stock quantity must be a whole number"),
  tax_rate: Yup.number()
    .min(0, "Tax rate must be greater than or equal to 0")
    .max(100, "Tax rate cannot exceed 100%"),
});

const ProductForm = ({ open, onClose, mode = "add", productData = null, onSubmit }) => {
  const [customFields, setCustomFields] = useState({});
  useEffect(() => {
    if (open) setCustomFields({});
  }, [open, productData?.product_id]);
  const initialValues = {
    product_name: productData?.product_name || "",
    product_code: productData?.product_code || "",
    description: productData?.description || "",
    mrp: productData?.mrp || "",
    selling_price: productData?.selling_price || "",
    stock_quantity: productData?.stock_quantity || 0,
    unit: productData?.unit || "PCS",
    category: productData?.category || "",
    barcode: productData?.barcode || "",
    tax_rate: productData?.tax_rate || 0,
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      const formattedValues = {
        ...values,
        mrp: parseFloat(values.mrp) || 0,
        selling_price: parseFloat(values.selling_price) || 0,
        stock_quantity: parseInt(values.stock_quantity) || 0,
        tax_rate: parseFloat(values.tax_rate) || 0,
        custom_fields: customFields,
      };
      await onSubmit(formattedValues);
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      if (error?.response?.data?.error?.message) {
        const errorMessage = error.response.data.error.message;
        if (errorMessage.includes('name')) {
          setFieldError('product_name', errorMessage);
        } else if (errorMessage.includes('price') || errorMessage.includes('MRP')) {
          setFieldError('mrp', errorMessage);
          setFieldError('selling_price', errorMessage);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "90%", md: "600px", lg: "700px" },
          maxWidth: "700px",
        },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
          <Form>
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              {/* Header */}
              <Box
                sx={{
                  p: 3,
                  borderBottom: "1px solid #e0e0e0",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {mode === "add" ? "Add New Product" : "Edit Product"}
                  </Typography>
                  <IconButton onClick={onClose} sx={{ color: "white" }}>
                    <MdClose />
                  </IconButton>
                </Box>
              </Box>

              {/* Form Content */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    name="product_name"
                    label="Product Name *"
                    value={values.product_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.product_name && !!errors.product_name}
                    helperText={touched.product_name && errors.product_name || "Enter the product name"}
                    placeholder="e.g., Premium Shampoo 500ml"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdInventory2 color="#667eea" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />

                  <TextField
                    name="product_code"
                    label="Product Code"
                    value={values.product_code}
                    onChange={handleChange}
                    placeholder="e.g., PROD001"
                    helperText="Unique product code (optional)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdNumbers color="#667eea" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />

                  <TextField
                    name="description"
                    label="Description"
                    value={values.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="e.g., Premium quality shampoo for all hair types"
                    helperText="Product description (optional)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdDescription color="#667eea" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      name="mrp"
                      label="MRP (₹) *"
                      type="number"
                      value={values.mrp}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.mrp && !!errors.mrp}
                      helperText={touched.mrp && errors.mrp || "Maximum Retail Price"}
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MdAttachMoney color="#667eea" />
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#667eea',
                          },
                        },
                      }}
                    />

                    <TextField
                      name="selling_price"
                      label="Selling Price (₹) *"
                      type="number"
                      value={values.selling_price}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.selling_price && !!errors.selling_price}
                      helperText={touched.selling_price && errors.selling_price || "Price at which product is sold"}
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MdAttachMoney color="#667eea" />
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#667eea',
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      name="stock_quantity"
                      label="Stock Quantity"
                      type="number"
                      value={values.stock_quantity}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.stock_quantity && !!errors.stock_quantity}
                      helperText={touched.stock_quantity && errors.stock_quantity || "Current stock available"}
                      placeholder="0"
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#667eea',
                          },
                        },
                      }}
                    />

                    <TextField
                      name="unit"
                      label="Unit"
                      select
                      value={values.unit}
                      onChange={handleChange}
                      helperText="Unit of measurement"
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#667eea',
                          },
                        },
                      }}
                    >
                      <MenuItem value="PCS">PCS (Pieces)</MenuItem>
                      <MenuItem value="KG">KG (Kilograms)</MenuItem>
                      <MenuItem value="L">L (Liters)</MenuItem>
                      <MenuItem value="M">M (Meters)</MenuItem>
                      <MenuItem value="BOX">BOX</MenuItem>
                      <MenuItem value="PKT">PKT (Packet)</MenuItem>
                    </TextField>
                  </Box>

                  <TextField
                    name="category"
                    label="Category"
                    value={values.category}
                    onChange={handleChange}
                    placeholder="e.g., Hair Care, Skincare"
                    helperText="Product category (optional)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdCategory color="#667eea" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />

                  <TextField
                    name="barcode"
                    label="Barcode"
                    value={values.barcode}
                    onChange={handleChange}
                    placeholder="e.g., 1234567890123"
                    helperText="Product barcode (optional)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdQrCode color="#667eea" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />

                  <TextField
                    name="tax_rate"
                    label="Tax Rate (%)"
                    type="number"
                    value={values.tax_rate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.tax_rate && !!errors.tax_rate}
                    helperText={touched.tax_rate && errors.tax_rate || "Tax percentage (0-100)"}
                    placeholder="0"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdPercent color="#667eea" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />
                </Box>
                <MoreFields
                  module="products"
                  recordId={mode === "edit" ? productData?.product_id : null}
                  values={customFields}
                  onChange={setCustomFields}
                />
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  p: 3,
                  borderTop: "1px solid #e0e0e0",
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={{
                    textTransform: 'none',
                    px: 3,
                    borderColor: '#d1d5db',
                    color: '#374151',
                    '&:hover': {
                      borderColor: '#9ca3af',
                      backgroundColor: '#f9fafb',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    textTransform: 'none',
                    px: 3,
                    backgroundColor: '#5c6ac4',
                    '&:hover': {
                      backgroundColor: '#4c56a8',
                    },
                  }}
                >
                  {isSubmitting ? 'Saving...' : mode === 'edit' ? 'Update Product' : 'Add Product'}
                </Button>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

export default ProductForm;

